import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faUserEdit, faTrash, faCommentMedical, faCalendarPlus,
  faFileAlt, faUpload, faDownload, faFilePdf, faFileImage, faInfoCircle,
  faFolder, faGripVertical, faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPatientById } from '../../store/clinics/clinicsSlice';
import { clinicsService, getAppointmentNotes, type Appointment, type PatientFile } from '../../services/clinicsService';
import ClinicsNewAppointmentModal from './ClinicsNewAppointmentModal';
import ClinicsEditPatientModal from './ClinicsEditPatientModal';
import { PatientTagsSelector } from '../../components/clinics/PatientTagsSelector';
import { SlidingPills } from '../../components/ui/SlidingPills';
import BtnTaratic from '../../components/ui/BtnTaratic';
import MoveToModal from '../../components/clinics/MoveToModal';
import { useNotifications } from '../../components/ui/notifications';
import { useDeleteConfirmation } from '../../components/ui/DeleteConfirmationProvider';

const ClinicsPatientDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const notifications = useNotifications();
  const confirmDelete = useDeleteConfirmation();
  const { selectedPatient: patient, loading, error } = useAppSelector((state) => state.clinics);

  const [activeTab, setActiveTab] = useState<'info' | 'appointments' | 'files' | 'chat'>('info');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [files, setFiles] = useState<PatientFile[]>([]);
  const [folders, setFolders] = useState<any[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);

  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState<any | null>(null);

  // ── Drag & Drop state ──────────────────────────────────────────────────────
  /** The item currently being dragged */
  const [draggedItem, setDraggedItem] = useState<{ type: 'file' | 'folder'; id: number; name: string } | null>(null);
  /** The folder ID that is currently highlighted as a drop target */
  const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);

  // ── Move To Modal state ───────────────────────────────────────────────────
  const [moveTarget, setMoveTarget] = useState<{ type: 'file' | 'folder'; id: number; name: string } | null>(null);

  // ── Toast state ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (id && !isNaN(Number(id))) {
      dispatch(fetchPatientById(Number(id)));
      loadAppointments(Number(id));
      loadFiles(Number(id));
      loadFolders(Number(id));
    }
  }, [dispatch, id]);

  // Polling for pending files
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    const hasPendingFiles = files.some(f => f.processing_status === 'pending');

    if (hasPendingFiles) {
      intervalId = setInterval(() => {
        if (id && !isNaN(Number(id))) {
          loadFiles(Number(id));
        }
      }, 3000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [files, id]);

  const loadAppointments = async (patientId: number) => {
    try {
      const resp = await clinicsService.getAppointments({ patient_id: patientId, per_page: 100 });
      setAppointments(resp.appointments);
    } catch (err) {
      console.error('Failed to load appointments', err);
    }
  };

  const loadFiles = async (patientId: number) => {
    try {
      const resp = await clinicsService.getPatientFiles(patientId);
      setFiles(resp);
    } catch (err) {
      console.error('Failed to load files', err);
    }
  };

  const loadFolders = async (patientId: number) => {
    try {
      const resp = await clinicsService.getPatientFolders(patientId);
      setFolders(resp);
    } catch (err) {
      console.error('Failed to load folders', err);
    }
  };

  const handleCreateFolder = async () => {
    if (!id || isNaN(Number(id))) return;
    if (!newFolderName || newFolderName.trim() === '') {
      setIsCreatingFolder(false);
      return;
    }
    try {
      const folderName = newFolderName.trim();
      await clinicsService.createPatientFolder(Number(id), { name: folderName, parent_id: currentFolderId });
      setNewFolderName('');
      setIsCreatingFolder(false);
      loadFolders(Number(id));
      notifications.success('Carpeta creada', `${folderName} se añadió correctamente.`);
    } catch (err) {
      notifications.error('No se pudo crear la carpeta', 'Inténtalo de nuevo.');
    }
  };

  const handleDeletePatient = async () => {
    if (!patient) return;
    if (await confirmDelete({
      title: 'Eliminar paciente',
      message: `¿Estás seguro de que deseas eliminar al paciente ${patient.full_name} permanentemente? Esto también eliminará sus citas y archivos.`,
    })) {
      try {
        await clinicsService.deletePatient(patient.id);
        navigate('/clinics/patients');
      } catch (err) {
        alert('Error al eliminar paciente.');
      }
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !id || isNaN(Number(id))) return;
    const file = e.target.files[0];
    const patientId = Number(id);

    setUploadingFile(true);
    try {
      await clinicsService.uploadFile(patientId, file, undefined, currentFolderId || undefined);
      loadFiles(patientId);
    } catch (err: any) {
      const errorMessage = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : 'Error al subir el archivo.';
      alert(errorMessage);
    } finally {
      setUploadingFile(false);
      e.target.value = ''; // reset input
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    if (!id || isNaN(Number(id))) return;
    const patientId = Number(id);
    if (await confirmDelete({ title: 'Eliminar archivo', message: '¿Deseas eliminar este archivo?' })) {
      try {
        await clinicsService.deleteFile(patientId, fileId);
        loadFiles(patientId);
      } catch (err) {
        alert('Error al eliminar el archivo.');
      }
    }
  };

  const handleDownloadFile = async (file: PatientFile) => {
    if (!id || isNaN(Number(id))) return;
    try {
      const blob = await clinicsService.downloadFile(Number(id), file.id);
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = file.file_name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch {
      alert('Error al descargar el archivo.');
    }
  };

  const handleDeleteFolder = async (folderId: number, folderName: string) => {
    if (!id || isNaN(Number(id))) return;
    if (!await confirmDelete({
      title: 'Eliminar carpeta',
      message: `¿Deseas eliminar la carpeta "${folderName}"? Los archivos internos pueden quedar huérfanos o eliminarse.`,
    })) return;
    try {
      await clinicsService.deletePatientFolder(Number(id), folderId);
      await loadFolders(Number(id));
    } catch (err) {
      alert('Error al eliminar la carpeta.');
    }
  };

  // ── Move handlers ──────────────────────────────────────────────────────────

  const handleMoveItem = async (destinationFolderId: number | null) => {
    if (!id || isNaN(Number(id)) || !moveTarget) return;
    const patientId = Number(id);
    try {
      if (moveTarget.type === 'file') {
        // Check for name conflict at destination
        const destFiles = files.filter(f => (f.folder_id ?? null) === destinationFolderId);
        if (destFiles.some(f => f.file_name === moveTarget.name && f.id !== moveTarget.id)) {
          notifications.warning('No se puede mover el archivo', `Ya existe un archivo llamado "${moveTarget.name}" en esa carpeta.`);
          return;
        }
        await clinicsService.moveFile(patientId, moveTarget.id, destinationFolderId);
        await loadFiles(patientId);
      } else {
        // Check for name conflict at destination
        const destFolders = folders.filter(f => (f.parent_id ?? null) === destinationFolderId);
        if (destFolders.some(f => f.name === moveTarget.name && f.id !== moveTarget.id)) {
          notifications.warning('No se puede mover la carpeta', `Ya existe una carpeta llamada "${moveTarget.name}" en ese destino.`);
          return;
        }
        await clinicsService.moveFolder(patientId, moveTarget.id, destinationFolderId);
        await loadFolders(patientId);
      }
    } catch (err) {
      notifications.error('No se pudo mover el elemento', 'Inténtalo de nuevo.');
    } finally {
      setMoveTarget(null);
    }
  };

  // ── Drag & Drop handlers ───────────────────────────────────────────────────

  const handleDragStart = (
    e: React.DragEvent,
    item: { type: 'file' | 'folder'; id: number; name: string }
  ) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverFolderId(null);
  };

  const handleFolderDragOver = (e: React.DragEvent, folderId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(folderId);
  };

  const handleFolderDragLeave = () => {
    setDragOverFolderId(null);
  };

  const handleDropOnFolder = async (e: React.DragEvent, targetFolderId: number) => {
    e.preventDefault();
    setDragOverFolderId(null);
    if (!draggedItem || !id || isNaN(Number(id))) return;
    const patientId = Number(id);

    // Can't drop a folder onto itself or its own child (guard)
    if (draggedItem.type === 'folder' && draggedItem.id === targetFolderId) {
      setDraggedItem(null);
      return;
    }

    try {
      if (draggedItem.type === 'file') {
        const destFiles = files.filter(f => (f.folder_id ?? null) === targetFolderId);
        if (destFiles.some(f => f.file_name === draggedItem.name && f.id !== draggedItem.id)) {
          notifications.warning('No se puede mover el archivo', `Ya existe un archivo llamado "${draggedItem.name}" en esa carpeta.`);
          setDraggedItem(null);
          return;
        }
        await clinicsService.moveFile(patientId, draggedItem.id, targetFolderId);
        await loadFiles(patientId);
      } else {
        // Prevent circular: can't move a folder into one of its descendants
        const destFolders = folders.filter(f => (f.parent_id ?? null) === targetFolderId);
        if (destFolders.some(f => f.name === draggedItem.name && f.id !== draggedItem.id)) {
          notifications.warning('No se puede mover la carpeta', `Ya existe una carpeta llamada "${draggedItem.name}" en ese destino.`);
          setDraggedItem(null);
          return;
        }
        await clinicsService.moveFolder(patientId, draggedItem.id, targetFolderId);
        await loadFolders(patientId);
      }
    } catch (err) {
      notifications.error('No se pudo mover el elemento', 'Inténtalo de nuevo.');
    } finally {
      setDraggedItem(null);
    }
  };


  if (loading && !patient) {
    return (
      <div data-ui="clinics-patient-detail-loading" className="page-theme-surface page-theme-clinics flex justify-center items-center py-20">
        <div data-ui="clinics-patient-detail-page-region-001" className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  const renderError = () => {
    if (typeof error === 'string') return error;
    if (error) return JSON.stringify(error);
    return 'Paciente no encontrado o ID inválido.';
  };

  if (error || !patient || isNaN(Number(id))) {
    return (
      <div data-ui="clinics-patient-detail-error" className="page-theme-surface page-theme-clinics p-8 text-center text-red-600">
        <p>{renderError()}</p>
        <Link to="/clinics/patients" className="text-blue-400 hover:underline mt-4 inline-block">Volver a pacientes</Link>
      </div>
    );
  }

  return (
    <div data-ui="clinics-patient-detail-page" className="page-theme-surface page-theme-clinics flex flex-col h-full w-full p-8 gap-8 overflow-y-auto custom-scrollbar relative">
      {/* Header Info Card */}
      <div data-ui="clinics-patient-detail-page-region-003" className="bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-6 flex flex-wrap items-center justify-between gap-4 relative z-10">
        <div data-ui="clinics-patient-detail-page-region-004" className="flex items-center gap-4 min-w-0">
          <Link to="/clinics/patients">
            <BtnTaratic color="ghost-dark" size="md" iconOnly title={t('common.back')}>
              <FontAwesomeIcon icon={faArrowLeft} className="text-sm" />
            </BtnTaratic>
          </Link>
          <div data-ui="clinics-patient-detail-page-region-005" className="flex items-center gap-4 min-w-0">
            {patient.avatar_url ? (
              <img src={patient.avatar_url} alt={patient.full_name} className="w-14 h-14 rounded-full object-cover border-2 border-white/20 shrink-0" />
            ) : (
              <div data-ui="clinics-patient-detail-page-region-006" className="w-14 h-14 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center font-bold text-xl border-2 border-blue-500/30 shrink-0">
                {patient.full_name.charAt(0).toUpperCase()}
              </div>
            )}
            <div data-ui="clinics-patient-detail-page-region-007" className="min-w-0">
              <h1 className="text-2xl font-bold text-white leading-tight truncate">{patient.full_name}</h1>
              <div data-ui="clinics-patient-detail-page-region-008" className="flex flex-wrap items-center gap-2 mt-1">
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${patient.status === 'active' ? 'bg-green-500/20 border-green-500/50 text-green-300' :
                  patient.status === 'inactive' ? 'bg-orange-500/20 border-orange-500/50 text-orange-300' :
                    'bg-gray-500/20 border-gray-500/50 text-gray-300'
                  }`}>
                  {patient.status === 'active' ? t('clinics.dashboard.status.active') : patient.status === 'inactive' ? t('clinics.dashboard.status.inactive') : t('clinics.dashboard.status.discharged')}
                </span>
                <span className="text-white/60 text-xs font-medium">
                  {t('clinics.patientDetail.registered')} {new Date(patient.registration_date).toLocaleDateString()}
                </span>
              </div>
              <div data-ui="clinics-patient-detail-page-region-009" className="mt-2">
                <PatientTagsSelector
                  patientId={patient.id}
                  assignedTags={patient.tags || []}
                  onTagsUpdated={() => dispatch(fetchPatientById(patient.id))}
                />
              </div>
            </div>
          </div>
        </div>
        <div data-ui="clinics-patient-detail-page-region-010" className="flex gap-2.5 self-start ml-auto">
          <BtnTaratic onClick={() => setIsEditModalOpen(true)} color="orange" size="md" iconOnly title={t('clinics.patientDetail.editPatient')}>
            <FontAwesomeIcon icon={faUserEdit} />
          </BtnTaratic>
          <BtnTaratic onClick={handleDeletePatient} color="red" size="md" iconOnly title={t('clinics.patientDetail.deletePatient')}>
            <FontAwesomeIcon icon={faTrash} />
          </BtnTaratic>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div data-ui="clinics-patient-detail-page-region-011" className="relative z-10">
        <SlidingPills
          options={[
            { id: 'info', label: t('clinics.patientDetail.tabs.medicalHistory'), icon: faInfoCircle },
            { id: 'appointments', label: `${t('clinics.patientDetail.tabs.appointments')} (${appointments.length})`, icon: faCalendarPlus },
            { id: 'files', label: `${t('clinics.patientDetail.tabs.documents')} (${files.length})`, icon: faFileAlt },
            { id: 'chat', label: t('clinics.patientDetail.medicalRagChat'), icon: faCommentMedical },
          ]}
          activeId={activeTab}
          onChange={(tabId) => {
            if (tabId === 'chat') {
              navigate(`/clinics/patients/${patient.id}/chat`);
            } else {
              setActiveTab(tabId as 'info' | 'appointments' | 'files');
            }
          }}
          variant="dark"
          size="md"
        />
      </div>

      <div data-ui="clinics-patient-detail-page-region-012" className="bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-6 relative z-10">
        {activeTab === 'info' && (
          <div data-ui="clinics-patient-detail-page-region-013" className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div data-ui="clinics-patient-detail-page-region-014" className="space-y-4">
              <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">{t('clinics.patientDetail.info.contact')}</h3>
              <div data-ui="clinics-patient-detail-page-region-015" className="grid grid-cols-2 gap-2 text-sm">
                <div data-ui="clinics-patient-detail-page-region-016" className="text-white/60">{t('clinics.patientDetail.info.email')}:</div><div data-ui="clinics-patient-detail-page-region-017" className="text-white">{patient.email || '—'}</div>
                <div data-ui="clinics-patient-detail-page-region-018" className="text-white/60">{t('clinics.patientDetail.info.phone')}:</div><div data-ui="clinics-patient-detail-page-region-019" className="text-white">{patient.phone || '—'}</div>
                <div data-ui="clinics-patient-detail-page-region-020" className="text-white/60">{t('clinics.patientDetail.info.address')}:</div><div data-ui="clinics-patient-detail-page-region-021" className="text-white">{patient.address || '—'}</div>
                <div data-ui="clinics-patient-detail-page-region-022" className="text-white/60">{t('clinics.patientDetail.info.birthDate')}:</div><div data-ui="clinics-patient-detail-page-region-023" className="text-white">{patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString() : '—'}</div>
                <div data-ui="clinics-patient-detail-page-region-024" className="text-white/60">Género:</div><div data-ui="clinics-patient-detail-page-region-025" className="text-white capitalize">{patient.gender || '—'}</div>
              </div>

              <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2 pt-4">{t('clinics.patientDetail.emergencyContact')}</h3>
              <div data-ui="clinics-patient-detail-page-region-026" className="grid grid-cols-2 gap-2 text-sm">
                <div data-ui="clinics-patient-detail-page-region-027" className="text-white/60">Nombre:</div><div data-ui="clinics-patient-detail-page-region-028" className="text-white">{patient.emergency_contact_name || '—'}</div>
                <div data-ui="clinics-patient-detail-page-region-029" className="text-white/60">Teléfono:</div><div data-ui="clinics-patient-detail-page-region-030" className="text-white">{patient.emergency_contact_phone || '—'}</div>
              </div>
            </div>

            <div data-ui="clinics-patient-detail-page-region-031" className="space-y-4">
              <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">{t('clinics.patientDetail.clinicalData.title')}</h3>
              <div data-ui="clinics-patient-detail-page-region-032" className="grid grid-cols-2 gap-2 text-sm">
                <div data-ui="clinics-patient-detail-page-region-033" className="text-white/60">{t('clinics.patientDetail.clinicalData.bloodType')}:</div><div data-ui="clinics-patient-detail-page-region-034" className="text-white font-bold text-red-400">{patient.blood_type || '—'}</div>
                <div data-ui="clinics-patient-detail-page-region-035" className="text-white/60">{t('clinics.patientDetail.clinicalData.allergies')}:</div><div data-ui="clinics-patient-detail-page-region-036" className="text-white">{patient.allergies || t('clinics.patientDetail.clinicalData.none')}</div>
                <div data-ui="clinics-patient-detail-page-region-037" className="text-white/60">{t('clinics.patientDetail.clinicalData.chronicConditions')}:</div><div data-ui="clinics-patient-detail-page-region-038" className="text-white">{patient.chronic_conditions || t('clinics.patientDetail.clinicalData.none')}</div>
              </div>

              <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2 pt-4">{t('clinics.patientDetail.medicalInsurance')}</h3>
              <div data-ui="clinics-patient-detail-page-region-039" className="grid grid-cols-2 gap-2 text-sm">
                <div data-ui="clinics-patient-detail-page-region-040" className="text-white/60">Proveedor:</div><div data-ui="clinics-patient-detail-page-region-041" className="text-white">{patient.insurance_provider || '—'}</div>
                <div data-ui="clinics-patient-detail-page-region-042" className="text-white/60">Número de Póliza:</div><div data-ui="clinics-patient-detail-page-region-043" className="text-white font-mono">{patient.insurance_policy_number || '—'}</div>
              </div>
            </div>

            {patient.notes && (
              <div data-ui="clinics-patient-detail-page-region-044" className="md:col-span-2 space-y-2 mt-4">
                <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">{t('clinics.patientDetail.additionalNotes')}</h3>
                <p className="text-white/80 text-sm whitespace-pre-wrap bg-black/20 p-4 rounded-lg border border-white/10">
                  {patient.notes}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'appointments' && (
          <div data-ui="clinics-patient-detail-page-region-045">
            <div data-ui="clinics-patient-detail-page-region-046" className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">{t('clinics.patientDetail.appointmentsTab.history')}</h3>
              <button onClick={() => setIsAppointmentModalOpen(true)} className="btn-taratic btn-green btn-xs flex items-center gap-2">
                <FontAwesomeIcon icon={faCalendarPlus} />
                {t('clinics.patientDetail.appointmentsTab.schedule')}
              </button>
            </div>

            {appointments.length === 0 ? (
              <div data-ui="clinics-patient-detail-page-region-047" className="p-8 text-center text-white/60 bg-black/20 rounded-xl border border-white/5">
                {t('clinics.patientDetail.appointmentsTab.noAppointments')}
              </div>
            ) : (
              <div data-ui="clinics-patient-detail-page-region-048" className="space-y-3">
                {appointments.map(appt => (
                  <div data-ui="clinics-patient-detail-page-region-049" key={appt.id} className="bg-black/20 border border-white/10 rounded-xl p-4 flex flex-col md:flex-row gap-4 justify-between transition-colors hover:bg-white/5">
                    <div data-ui="clinics-patient-detail-page-region-050">
                      <div data-ui="clinics-patient-detail-page-region-051" className="flex items-center gap-3 mb-1">
                        <span className="font-bold text-white text-lg">{new Date(appt.start_time).toLocaleDateString()}</span>
                        <span className="text-white/60 text-sm">{new Date(appt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold border ${appt.status === 'completed' ? 'bg-green-500/20 border-green-500/50 text-green-300' :
                          appt.status === 'in-progress' ? 'bg-orange-500/20 border-orange-500/50 text-orange-300' :
                            appt.status === 'scheduled' ? 'bg-blue-500/20 border-blue-500/50 text-blue-300' :
                              'bg-gray-500/20 border-gray-500/50 text-gray-300'
                          }`}>
                          {appt.status}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] uppercase font-bold border bg-purple-500/20 border-purple-500/50 text-purple-300">
                          {appt.appointment_type}
                        </span>
                      </div>
                      <div data-ui="clinics-patient-detail-page-region-052" className="text-sm text-white/80 line-clamp-2 mt-2">
                        <span className="font-semibold text-white">{t('clinics.patientDetail.appointmentsTab.reason')} </span> {getAppointmentNotes(appt) || '—'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          <div data-ui="clinics-patient-detail-page-region-053">
            <div data-ui="clinics-patient-detail-page-region-054" className="flex justify-between items-center mb-6">
              <div data-ui="clinics-patient-detail-page-region-055" className="flex items-center gap-3">
                <h3 className="text-lg font-bold text-white">{t('clinics.patientDetail.filesTab.title')}</h3>
                {currentFolderId && (
                  <button onClick={() => {
                    const currentFolder = folders.find(f => f.id === currentFolderId);
                    setCurrentFolderId(currentFolder?.parent_id || null);
                  }} className="btn-taratic btn-ghost-dark btn-xs flex items-center gap-2">
                    <FontAwesomeIcon icon={faArrowLeft} /> Volver
                  </button>
                )}
                <span className="text-white/50 text-sm">
                  / {currentFolderId ? folders.find(f => f.id === currentFolderId)?.name : 'Raíz'}
                </span>
              </div>
              <div data-ui="clinics-patient-detail-page-region-056" className="flex items-center gap-2">
                {isCreatingFolder ? (
                  <div data-ui="clinics-patient-detail-page-region-057" className="flex items-center gap-1">
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateFolder();
                        if (e.key === 'Escape') {
                          setIsCreatingFolder(false);
                          setNewFolderName('');
                        }
                      }}
                      autoFocus
                      placeholder="Nombre..."
                      className="bg-black/75 border border-white/20 text-white text-xs px-2 py-1.5 rounded-lg focus:outline-none focus:border-white/50 w-32"
                    />
                    <button onClick={handleCreateFolder} className="btn-taratic btn-blue btn-xs border px-2 py-1.5 flex items-center justify-center">
                      ✓
                    </button>
                    <button onClick={() => { setIsCreatingFolder(false); setNewFolderName(''); }} className="btn-taratic btn-xs bg-red-500/20 text-red-300 border border-red-500/50 hover:bg-red-500/40 px-2 py-1.5 flex items-center justify-center">
                      ✕
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setIsCreatingFolder(true)} className="btn-taratic btn-blue btn-xs border flex items-center gap-2">
                    <FontAwesomeIcon icon={faFolder} /> Nueva Carpeta
                  </button>
                )}
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                />
                <label htmlFor="file-upload" className={`btn-taratic btn-blue btn-xs flex items-center gap-2 cursor-pointer ${uploadingFile ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {uploadingFile ? (
                    <div data-ui="clinics-patient-detail-page-region-058" className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <FontAwesomeIcon icon={faUpload} />
                  )}
                  {uploadingFile ? t('clinics.patientDetail.filesTab.uploading') : t('clinics.patientDetail.filesTab.uploadFile')}
                </label>
              </div>
            </div>

            {/* FOLDERS AND FILES SECTION */}
            <div data-ui="clinics-patient-detail-page-region-059" className="space-y-6">
              {(() => {
                const visibleFolders = folders.filter(f => (f.parent_id || null) === currentFolderId);
                if (visibleFolders.length > 0) {
                  return (
                    <div data-ui="clinics-patient-detail-page-region-060">
                      <div data-ui="clinics-patient-detail-page-region-061" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                        {visibleFolders.map(folder => (
                          <div data-ui="clinics-patient-detail-page-region-062"
                            key={folder.id}
                            /* ── drag source ── */
                            draggable
                            onDragStart={(e) => handleDragStart(e, { type: 'folder', id: folder.id, name: folder.name })}
                            onDragEnd={handleDragEnd}
                            /* ── drop target ── */
                            onDragOver={(e) => handleFolderDragOver(e, folder.id)}
                            onDragLeave={handleFolderDragLeave}
                            onDrop={(e) => handleDropOnFolder(e, folder.id)}
                            onClick={() => setCurrentFolderId(folder.id)}
                            className={[
                              'flex flex-col items-center gap-2 border rounded-xl p-3 cursor-pointer transition-all group relative',
                              dragOverFolderId === folder.id
                                ? 'bg-blue-500/20 border-blue-400/70 shadow-lg shadow-blue-500/20 scale-105'
                                : draggedItem?.id === folder.id && draggedItem?.type === 'folder'
                                  ? 'opacity-40 border-white/10 bg-black/75'
                                  : 'bg-black/75 border-white/10 hover:bg-white/10',
                            ].join(' ')}
                          >
                            {/* Grip handle */}
                            <div data-ui="clinics-patient-detail-page-region-063"
                              className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-40 hover:!opacity-80 text-white/60 cursor-grab active:cursor-grabbing transition-opacity"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <FontAwesomeIcon icon={faGripVertical} className="text-[10px]" />
                            </div>

                            {/* Delete button */}
                            <button onClick={(e) => {
                              e.stopPropagation();
                              void handleDeleteFolder(folder.id, folder.name);
                            }} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-500">
                              <FontAwesomeIcon icon={faTrash} className="text-xs" />
                            </button>

                            {/* Move to button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMoveTarget({ type: 'folder', id: folder.id, name: folder.name });
                              }}
                              title="Mover a…"
                              className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded bg-white/10 text-white/50 hover:text-white hover:bg-white/20 transition-all"
                            >
                              <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
                            </button>

                            <FontAwesomeIcon
                              icon={faFolder}
                              className={[
                                'text-3xl transition-colors',
                                dragOverFolderId === folder.id ? 'text-blue-300' : 'text-blue-400 group-hover:text-blue-300',
                              ].join(' ')}
                            />
                            <span className="text-white/80 font-medium text-xs text-center line-clamp-2">{folder.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {(() => {
                const visibleFiles = files.filter(f => (f.folder_id || null) === currentFolderId);
                if (visibleFiles.length === 0) {
                  return (
                    <div data-ui="clinics-patient-detail-page-region-064" className="p-8 text-center text-white/60 bg-black/20 rounded-xl border border-white/5">
                      {t('clinics.patientDetail.filesTab.noFiles')}
                    </div>
                  );
                }
                return (
                  <div data-ui="clinics-patient-detail-page-region-065" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {visibleFiles.map(file => {
                      const isPdf = file.file_type === 'pdf' || file.file_name.endsWith('.pdf');
                      const isImage = file.file_type === 'image' || file.file_name.match(/\.(jpeg|jpg|gif|png)$/i);
                      const isBeingDragged = draggedItem?.id === file.id && draggedItem?.type === 'file';

                      return (
                        <div data-ui="clinics-patient-detail-page-region-066"
                          key={file.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, { type: 'file', id: file.id, name: file.file_name })}
                          onDragEnd={handleDragEnd}
                          className={[
                            'bg-black/75 border border-white/10 rounded-xl p-4 flex flex-col group transition-all hover:bg-white/10 hover:border-white/20 relative',
                            isBeingDragged ? 'opacity-40 scale-95' : '',
                          ].join(' ')}
                        >
                          {/* Grip handle */}
                          <div data-ui="clinics-patient-detail-page-region-067" className="absolute top-2 left-2 opacity-0 group-hover:opacity-40 hover:!opacity-80 text-white/60 cursor-grab active:cursor-grabbing transition-opacity">
                            <FontAwesomeIcon icon={faGripVertical} className="text-[10px]" />
                          </div>

                          <div data-ui="clinics-patient-detail-page-region-068" className="flex-1 flex flex-col items-center justify-center py-6 text-center">
                            <div data-ui="clinics-patient-detail-page-region-069" className="relative mb-3">
                              <FontAwesomeIcon
                                icon={isPdf ? faFilePdf : isImage ? faFileImage : faFileAlt}
                                className={`text-4xl ${isPdf ? 'text-red-400' : isImage ? 'text-blue-400' : 'text-gray-400'} ${file.processing_status === 'pending' ? 'opacity-50' : ''}`}
                              />
                              {file.processing_status === 'pending' && (
                                <div data-ui="clinics-patient-detail-page-region-070" className="absolute -top-1 -right-1 w-4 h-4 border-2 border-white/30 border-t-blue-400 rounded-full animate-spin"></div>
                              )}
                            </div>
                            <div data-ui="clinics-patient-detail-page-region-071" className="text-sm font-medium text-white truncate w-full px-2" title={file.file_name}>
                              {file.file_name}
                            </div>
                            <div data-ui="clinics-patient-detail-page-region-072" className="text-xs text-white/50 mt-1">
                              {file.file_size_bytes ? `${(file.file_size_bytes / 1024 / 1024).toFixed(2)} MB` : t('clinics.patientDetail.filesTab.unknown')}
                            </div>
                          </div>
                          <div data-ui="clinics-patient-detail-page-region-073" className="flex justify-between items-center mt-2 pt-2 border-t border-white/10">
                            <div data-ui="clinics-patient-detail-page-region-074" className="text-[10px] text-white/40">
                              {new Date(file.uploaded_at).toLocaleDateString()}
                            </div>
                            <div data-ui="clinics-patient-detail-page-region-075" className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {file.processing_status === 'pending' ? (
                                <div data-ui="clinics-patient-detail-page-region-076" className="w-7 h-7 flex items-center justify-center">
                                  <div data-ui="clinics-patient-detail-page-region-077" className="w-4 h-4 border-2 border-white/30 border-t-blue-400 rounded-full animate-spin" title={t('clinics.patientDetail.filesTab.uploading')}></div>
                                </div>
                              ) : (
                                <button onClick={() => setSelectedMetadata(file.extracted_metadata || { message: "Sin metadatos disponibles." })} className="w-7 h-7 rounded bg-purple-500/20 text-purple-300 flex items-center justify-center hover:bg-purple-500/40" title={t('clinics.patientDetail.filesTab.viewMetadata')}>
                                  <FontAwesomeIcon icon={faInfoCircle} className="text-xs" />
                                </button>
                              )}
                              {/* Move to button */}
                              <button
                                onClick={() => setMoveTarget({ type: 'file', id: file.id, name: file.file_name })}
                                className="w-7 h-7 rounded bg-white/10 text-white/60 flex items-center justify-center hover:bg-white/20 hover:text-white"
                                title="Mover a…"
                              >
                                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                              </button>
                              <button onClick={() => handleDownloadFile(file)} className="w-7 h-7 rounded bg-blue-500/20 text-blue-300 flex items-center justify-center hover:bg-blue-500/40" title={t('clinics.patientDetail.filesTab.downloadView')}>
                                <FontAwesomeIcon icon={faDownload} className="text-xs" />
                              </button>
                              <button onClick={() => handleDeleteFile(file.id)} className="w-7 h-7 rounded bg-red-600 text-white flex items-center justify-center hover:bg-red-700" title={t('clinics.patientDetail.filesTab.delete')}>
                                <FontAwesomeIcon icon={faTrash} className="text-xs" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {isAppointmentModalOpen && (
        <ClinicsNewAppointmentModal
          preselectedPatientId={Number(id)}
          onClose={() => setIsAppointmentModalOpen(false)}
          onSuccess={() => {
            setIsAppointmentModalOpen(false);
            loadAppointments(Number(id));
            dispatch(fetchPatientById(Number(id))); // reload to update counts
          }}
        />
      )}

      {isEditModalOpen && patient && (
        <ClinicsEditPatientModal
          patient={patient}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            setIsEditModalOpen(false);
            dispatch(fetchPatientById(Number(id))); // reload patient data
          }}
        />
      )}

      {selectedMetadata && (
        <div data-ui="clinics-patient-detail-page-region-078" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div data-ui="clinics-patient-detail-page-region-079" className="bg-[#0f1115] border border-white/10 rounded-3xl w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl p-6">
            <div data-ui="clinics-patient-detail-page-region-080" className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">{t('clinics.patientDetail.filesTab.metadataTitle')}</h3>
              <button onClick={() => setSelectedMetadata(null)} className="text-white/60 hover:text-white text-2xl font-bold">&times;</button>
            </div>
            <div data-ui="clinics-patient-detail-page-region-081" className="bg-black/30 rounded-xl p-4 font-mono text-sm text-green-300 overflow-x-auto border border-white/5">
              <pre>{JSON.stringify(selectedMetadata, null, 2)}</pre>
            </div>
            <div data-ui="clinics-patient-detail-page-region-082" className="mt-6 flex justify-end">
              <button onClick={() => setSelectedMetadata(null)} className="btn-taratic px-6 py-2 bg-white/10 border border-white/20 text-white hover:bg-white/20 rounded-full">
                {t('clinics.patientDetail.filesTab.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move To Modal */}
      {moveTarget && (
        <MoveToModal
          itemName={moveTarget.name}
          itemType={moveTarget.type}
          itemId={moveTarget.id}
          folders={folders}
          onMove={handleMoveItem}
          onClose={() => setMoveTarget(null)}
        />
      )}

    </div>
  );
};

export default ClinicsPatientDetailPage;
